import { Component, type ReactNode, type ErrorInfo } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? (
        <div className="min-h-[40vh] flex items-center justify-center text-center px-4">
          <div>
            <p className="text-base font-medium text-foreground mb-2">Something went wrong</p>
            {import.meta.env.DEV && this.state.error && (
              <pre className="text-xs text-left bg-muted rounded p-3 mb-4 max-w-xl overflow-auto text-destructive">
                {this.state.error.message}
              </pre>
            )}
            <p className="text-sm text-muted-foreground mb-4">
              An unexpected error occurred while loading this page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="text-sm text-primary hover:text-primary/80 font-medium"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
